import { ShipmentDelivery } from '../models/delivery/shipment-delivery.model';
import { ShipmentJobStatus, jobStatusInfoMap } from '../models/delivery/shipment-job.status';
import {
    ShipmentDeliveryOperationStatus,
    ShipmentOperationStopStatus
} from '../models/workflow/shipment-workflow.model';

export interface DeliveryOperationProgress {
    percent: number;
    caption: string;
    completedSites: number;
    totalSites: number;
    status: string;
}

const TERMINAL_DELIVERY_STATES = new Set([
    ShipmentJobStatus.DRIVER_JOB_COMPLETED,
    ShipmentJobStatus.JOB_COMPLETION_ACKNOWLEDGE,
    'COMPLETED',
    'DELIVERED',
    'DELIVERY_COMPLETE',
    'CUSTOMER_ACKNOWLEDGED',
    'CLOSED'
]);

const CANCELLED_DELIVERY_STATES = new Set([
    ShipmentJobStatus.JOB_DRIVER_REJECTED,
    ShipmentJobStatus.JOB_DRIVER_CANCELLED,
    'CANCELLED'
]);

const COMPLETE_SITE_STATES = new Set(['COMPLETE', 'COMPLETED', 'SKIPPED']);

const SITE_STATE_PERCENT: Record<string, number> = {
    PENDING: 0,
    PLANNED: 0,
    ASSIGNED: 0,
    ARRIVED: 50,
    AT_SITE: 50,
    WORKING: 75,
    IN_PROGRESS: 75,
    LOADING: 75,
    UNLOADING: 75,
    EVIDENCE_PENDING: 75,
    EXCEPTION: 75,
    COMPLETE: 100,
    COMPLETED: 100,
    SKIPPED: 100
};

export function calculateDeliveryOperationProgress(
    delivery?: ShipmentDelivery,
    operationStatus?: ShipmentDeliveryOperationStatus,
    assigned = false
): DeliveryOperationProgress {
    const status = normalizedState(
        operationStatus?.deliveryStatus
        || delivery?.workflowState
        || delivery?.jobStatus
    );
    const movementStops = uniqueMovementStops(operationStatus?.stops ?? []);
    const completedSites = movementStops.filter(stop => isSiteComplete(stop.workflowState)).length;

    if (TERMINAL_DELIVERY_STATES.has(status)) {
        return {
            percent: 100,
            caption: movementStops.length
                ? `${movementStops.length}/${movementStops.length} sites complete`
                : formatOperationalState(status),
            completedSites: movementStops.length,
            totalSites: movementStops.length,
            status
        };
    }

    if (CANCELLED_DELIVERY_STATES.has(status)) {
        return {
            percent: 0,
            caption: formatOperationalState(status),
            completedSites,
            totalSites: movementStops.length,
            status
        };
    }

    if (movementStops.length) {
        const stopProgress = movementStops.reduce(
            (total, stop) => total + siteStatePercent(stop.workflowState),
            0
        );
        const percent = Math.min(95, Math.round(stopProgress / movementStops.length));
        const activeStop = operationStatus?.activeStop
            || movementStops.find(stop => !isSiteComplete(stop.workflowState))
            || operationStatus?.nextStop;
        const activeLabel = activeStop
            ? ` · ${formatStopLabel(activeStop)} ${formatOperationalState(activeStop.workflowState).toLowerCase()}`
            : '';

        return {
            percent,
            caption: `${completedSites}/${movementStops.length} sites complete${activeLabel}`,
            completedSites,
            totalSites: movementStops.length,
            status
        };
    }

    const aggregateProgress = combinedAggregateProgress(operationStatus);
    if (aggregateProgress != null) {
        return {
            percent: Math.min(95, aggregateProgress),
            caption: operationStatus?.currentPhase
                ? formatOperationalState(operationStatus.currentPhase)
                : formatOperationalState(status),
            completedSites: 0,
            totalSites: 0,
            status
        };
    }

    const legacyPercent = legacyDeliveryProgress(status, assigned);
    return {
        percent: legacyPercent,
        caption: status ? formatOperationalState(status) : 'Assignment pending',
        completedSites: 0,
        totalSites: 0,
        status
    };
}

export function isDeliveryOperationComplete(status?: string): boolean {
    return TERMINAL_DELIVERY_STATES.has(normalizedState(status));
}

export function formatOperationalState(value?: string): string {
    return (value || 'Pending')
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, character => character.toUpperCase());
}

function uniqueMovementStops(stops: ShipmentOperationStopStatus[]): ShipmentOperationStopStatus[] {
    const unique = new Map<string, ShipmentOperationStopStatus>();
    stops
        .filter(stop => ['PICKUP', 'DROPOFF'].includes(normalizedState(stop.stopType)))
        .forEach((stop, index) => {
            const key = stop.stopId != null
                ? `id:${stop.stopId}`
                : `${normalizedState(stop.stopType)}:${stop.stopNumber ?? index}`;
            unique.set(key, stop);
        });
    return Array.from(unique.values());
}

function siteStatePercent(state?: string): number {
    return SITE_STATE_PERCENT[normalizedState(state)] ?? 0;
}

function isSiteComplete(state?: string): boolean {
    return COMPLETE_SITE_STATES.has(normalizedState(state));
}

function formatStopLabel(stop: ShipmentOperationStopStatus): string {
    const type = formatOperationalState(stop.stopType);
    return stop.stopNumber ? `${type} #${stop.stopNumber}` : type;
}

function combinedAggregateProgress(operationStatus?: ShipmentDeliveryOperationStatus): number | undefined {
    const progress = [
        operationStatus?.pickupProgress?.percent,
        operationStatus?.dropoffProgress?.percent
    ].filter((value): value is number => Number.isFinite(value));
    if (!progress.length) {
        return undefined;
    }
    return Math.round(progress.reduce((total, value) => total + value, 0) / progress.length);
}

function legacyDeliveryProgress(status: string, assigned: boolean): number {
    const statusInfo = jobStatusInfoMap[status as ShipmentJobStatus];
    if (!statusInfo || statusInfo.position > 26) {
        return assigned ? 10 : 0;
    }
    return Math.max(5, Math.min(95, Math.round((statusInfo.position / 26) * 100)));
}

function normalizedState(value?: string): string {
    return String(value || '').trim().toUpperCase();
}
